import React, { Component } from "react";
import {
  loading,
  NavbarAdmin,
  GET,
  DateRangeFields,
  t // นำเข้าฟังก์ชันแปลภาษา
} from "../../components/CustomComponent.js";
import { dateToKey, keyToDate } from "../../components/DateRangeFields.js"; // ปรับ Path ให้ตรงกับโปรเจกต์ของคุณ
import { formatNumber } from "../../utils/formatting";

const MONTH_SHORT_KEYS = ["jan_short", "feb_short", "mar_short", "apr_short", "may_short", "jun_short", "jul_short", "aug_short", "sep_short", "oct_short", "nov_short", "dec_short"];
const CATEGORY_COLORS = ["#3b82f6", "#f97316", "#a855f7", "#22c55e", "#ef4444", "#06b6d4", "#eab308", "#64748b"];

export default class CostReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data_work_orders: [],
      start_date: this.getDefaultStartDate(),
      end_date: this.getDefaultEndDate(),
    };
  }

  async componentDidMount() {
    loading(true);
    const woResult = await GET("admin/work-orders", { page: 0 });
    this.setState({
      data_work_orders: woResult?.status ? woResult.data || [] : [],
    });
    loading(false);
  }

  getDefaultStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  getDefaultEndDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  toDateOnly = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  getFilteredWorkOrders = () => {
    const { data_work_orders, start_date, end_date } = this.state;
    const rangeStart = this.toDateOnly(start_date);
    const rangeEnd = this.toDateOnly(end_date || start_date);
    if (!rangeStart || !rangeEnd) return data_work_orders;

    return data_work_orders.filter((item) => {
      const createdDate = this.toDateOnly(item.created_at);
      if (!createdDate) return false;
      return createdDate >= rangeStart && createdDate <= rangeEnd;
    });
  };

  // ต้นทุนของใบสั่งงาน 1 ใบ: parts = visits_items.price*qty (ของจริง), other = travel_cost + other_costs (ค่าใช้จ่ายเบ็ดเตล็ด/ผู้รับเหมาที่บันทึกต่อ visit — ไม่มี field "external cost" แยกต่างหากในระบบ)
  getWoCost = (wo) => {
    let partsCost = 0;
    let otherCost = 0;

    (wo.visits || []).forEach((visit) => {
      (visit.visits_items || []).forEach((vi) => {
        const qty = Number(vi.actual_quantity ?? vi.quantity) || 0;
        const price = Number(vi.price) || 0;
        partsCost += qty * price;
      });
      otherCost += Number(visit.travel_cost) || 0;
      (visit.other_costs || []).forEach((oc) => {
        otherCost += Number(oc.amount) || 0;
      });
    });

    return { partsCost, otherCost, totalCost: partsCost + otherCost };
  };

  getCategoryLabel = (wo) => wo?.category?.name_th || wo?.category?.name_en || "-";

  getWoTitle = (wo) => wo?.root_cause || wo?.wo_number || "-";

  getAssetLabel = (wo) => wo?.asset?.part?.name || wo?.asset?.asset_code || wo?.asset?.serial_number || "-";

  formatCurrency = (value) => `${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ฿`;

  getCategoryBreakdown = (filtered) => {
    const map = new Map();
    filtered.forEach((wo) => {
      const label = this.getCategoryLabel(wo);
      const cost = this.getWoCost(wo);
      const entry = map.get(label) || { label, color: wo?.category?.background_color || null, parts: 0, other: 0 };
      entry.parts += cost.partsCost;
      entry.other += cost.otherCost;
      map.set(label, entry);
    });

    return [...map.values()]
      .map((e) => ({ ...e, total: e.parts + e.other }))
      .filter((e) => e.total > 0)
      .sort((a, b) => b.total - a.total)
      .map((e, idx) => ({ ...e, color: e.color || CATEGORY_COLORS[idx % CATEGORY_COLORS.length] }));
  };

  buildDonutGradient = (categoryBreakdown, total) => {
    if (!total) return "#e9ecef";
    let cursor = 0;
    const stops = categoryBreakdown.map((c) => {
      const start = cursor;
      cursor += (c.total / total) * 100;
      return `${c.color} ${start}% ${cursor}%`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  };

  getTopCostDrivers = (filtered) => {
    return filtered
      .map((wo) => ({ wo, cost: this.getWoCost(wo) }))
      .filter((item) => item.cost.totalCost > 0)
      .sort((a, b) => b.cost.totalCost - a.cost.totalCost)
      .slice(0, 5)
      .map((item) => ({
        uid: item.wo.uid,
        title: this.getWoTitle(item.wo),
        asset: this.getAssetLabel(item.wo),
        cost: this.formatCurrency(item.cost.totalCost),
      }));
  };

  // เทรนด์ต้นทุนย้อนหลัง 6 เดือน (ไม่ผูกกับช่วงวันที่ที่เลือก เพราะเทรนด์ต้องดูหลายเดือนเสมอ)
  getMonthlyTrend = () => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth(), label: t(MONTH_SHORT_KEYS[d.getMonth()]) });
    }

    return months.map((m) => {
      let parts = 0;
      let other = 0;
      (this.state.data_work_orders || []).forEach((wo) => {
        const createdDate = wo.created_at ? new Date(wo.created_at) : null;
        if (!createdDate || createdDate.getFullYear() !== m.year || createdDate.getMonth() !== m.month) return;
        const cost = this.getWoCost(wo);
        parts += cost.partsCost;
        other += cost.otherCost;
      });
      return { label: m.label, parts, other, total: parts + other };
    });
  };

  render() {
    const { start_date, end_date } = this.state;
    const filtered = this.getFilteredWorkOrders();

    let totalParts = 0;
    let totalOther = 0;
    let woWithCostCount = 0;
    filtered.forEach((wo) => {
      const cost = this.getWoCost(wo);
      if (cost.totalCost > 0) woWithCostCount += 1;
      totalParts += cost.partsCost;
      totalOther += cost.otherCost;
    });
    const totalCost = totalParts + totalOther;

    const categoryBreakdown = this.getCategoryBreakdown(filtered);
    const donutGradient = this.buildDonutGradient(categoryBreakdown, totalCost);

    const trend = this.getMonthlyTrend();
    const trendMax = Math.max(1, ...trend.map((m) => m.total));

    const topCostDrivers = this.getTopCostDrivers(filtered);

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">

          {/* Header Title */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1">{t("cost_report")}</h3>
              <div className="text-secondary">{t("cost_report_desc")}</div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <DateRangeFields
                  type="th"
                  className="form-control bg-white rounded-2 border-0 shadow-sm text-dark"
                  fieldStyle={{ width: 150 }}
                  startPlaceholder={t("start_date")}
                  endPlaceholder={t("end_date")}
                  startValue={dateToKey(start_date)}
                  endValue={dateToKey(end_date)}
                  onChange={({ start, end }) =>
                    this.setState({ start_date: keyToDate(start), end_date: keyToDate(end) })
                  }
                />
              </div>
            </div>
          </div>

          {/* Top KPI Cards */}
          <div className="row g-3 mb-4">
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#f0fdfa" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("total_cost")}</div>
                <h1 className="fw-bold mb-0" style={{ fontSize: "2.25rem", color: "#16a34a" }}>{this.formatCurrency(totalCost)}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#fff7ed" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("parts_cost")}</div>
                <h1 className="fw-bold mb-0" style={{ color: "#f97316", fontSize: "2.25rem" }}>{this.formatCurrency(totalParts)}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#faf5ff" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("external_cost")}</div>
                <h1 className="fw-bold mb-0" style={{ color: "#a855f7", fontSize: "2.25rem" }}>{this.formatCurrency(totalOther)}</h1>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#eff6ff" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("total_wo")}</div>
                <h1 className="fw-bold mb-0" style={{ color: "#3b82f6", fontSize: "2.25rem" }}>{formatNumber(woWithCostCount)}</h1>
              </div>
            </div>
          </div>

          {/* Charts Row: Cost by Category & Monthly Trend */}
          <div className="row g-4 mb-4">

            {/* Cost by Category (Donut Chart) - อ้างอิงจาก work_orders.category จริง */}
            <div className="col-lg-5">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white">
                <h6 className="fw-bold text-dark mb-4">{t("cost_by_category")}</h6>

                <div className="d-flex align-items-center justify-content-center h-100 pb-3 gap-5">
                  <div
                    className="rounded-circle position-relative flex-shrink-0"
                    style={{
                      width: "240px", height: "240px",
                      background: donutGradient
                    }}
                  >
                    <div className="position-absolute top-50 start-50 translate-middle bg-white rounded-circle" style={{ width: "120px", height: "120px" }}></div>
                  </div>

                  <div className="d-flex flex-column gap-3 text-14 text-secondary">
                    {categoryBreakdown.length > 0 ? (
                      categoryBreakdown.slice(0, 6).map((c) => (
                        <div className="d-flex align-items-center" key={c.label}>
                          <div className="wpx-30 hpx-10 rounded me-3" style={{ backgroundColor: c.color }}></div>
                          {c.label} ({this.formatCurrency(c.total)})
                        </div>
                      ))
                    ) : (
                      <div>{t("no_data")}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Cost Trend (Stacked Bar Chart) - parts + other cost ย้อนหลัง 6 เดือนจริง */}
            <div className="col-lg-7">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white">
                <h6 className="fw-bold text-dark mb-4">{t("monthly_cost_trend")}</h6>

                <div className="d-flex flex-column h-100 justify-content-end pb-2">
                  <div className="position-relative w-100 pb-4" style={{ height: "240px" }}>

                    <div className="position-absolute top-0 start-0 d-flex flex-column justify-content-between h-100 text-secondary text-10 text-end pe-2" style={{ width: "70px", bottom: "25px" }}>
                      {[5, 4, 3, 2, 1, 0].map((i) => (
                        <span key={i}>{this.formatCurrency(Math.round((trendMax * i) / 5))}</span>
                      ))}
                    </div>

                    <div className="position-absolute h-100 d-flex align-items-end justify-content-around border-bottom border-start ps-3 pb-0" style={{ left: "70px", right: "0", bottom: "25px" }}>
                      <div className="w-100 h-100 d-flex flex-column justify-content-between position-absolute top-0 start-0 z-0">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className="border-top w-100" style={{ borderColor: "#f1f5f9" }}></div>
                        ))}
                      </div>

                      {trend.map((m, idx) => (
                        <div key={idx} className="z-1 d-flex flex-column justify-content-end" style={{ width: "12%", height: `${(m.total / trendMax) * 100}%` }}>
                          <div style={{ height: m.total ? `${(m.other / m.total) * 100}%` : "0%", backgroundColor: "#a855f7" }}></div>
                          <div style={{ height: m.total ? `${(m.parts / m.total) * 100}%` : "0%", backgroundColor: "#f97316" }}></div>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex justify-content-around position-absolute w-100 text-secondary text-12 ps-3" style={{ left: "70px", right: "0", bottom: "-25px" }}>
                      {trend.map((m, idx) => (
                        <span key={idx} style={{ width: "12%", textAlign: "center" }}>{m.label}</span>
                      ))}
                    </div>
                  </div>

                  <div className="d-flex justify-content-center gap-4 text-12 text-secondary mt-2">
                    <div className="d-flex align-items-center"><div className="wpx-30 hpx-8 rounded me-2" style={{ backgroundColor: "#f97316" }}></div> {t("parts")}</div>
                    <div className="d-flex align-items-center"><div className="wpx-30 hpx-8 rounded me-2" style={{ backgroundColor: "#a855f7" }}></div> {t("external")}</div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Top Cost Drivers */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white" style={{ backgroundColor: "#fffcf2" }}>
                <h6 className="fw-bold mb-4 d-flex align-items-center" style={{ color: "#b45309" }}>
                  <span className="icon text-danger me-2 text-18">{""}</span> {t("top_cost_drivers")}
                </h6>

                <div className="row g-3">
                  {topCostDrivers.length > 0 ? (
                    topCostDrivers.map((item) => (
                      <div className="col-lg-4 col-md-6" key={item.uid}>
                        <div className="d-flex justify-content-between align-items-center bg-white border rounded-3 p-3 shadow-sm h-100">
                          <div>
                            <div className="fw-bold text-dark text-16 mb-1">{item.title}</div>
                            <div className="text-secondary text-12">{item.asset}</div>
                          </div>
                          <div className="fw-bold text-danger text-18 text-nowrap ms-2">
                            {item.cost}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center text-secondary py-4">{t("no_data")}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cost Breakdown Table */}
          <div className="card border-0 rounded-4 shadow-sm mb-4 overflow-hidden bg-white">
            <div className="p-4 border-bottom">
              <h6 className="fw-bold text-dark mb-0">{t("cost_breakdown")}</h6>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr className="text-dark fw-bold text-14">
                    <th className="py-3 px-4 border-0 wpx-150">{t("category")}</th>
                    <th className="py-3 border-0 text-end">{t("parts")}</th>
                    <th className="py-3 border-0 text-end">{t("external")}</th>
                    <th className="py-3 border-0 text-end pe-4">{t("total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryBreakdown.length > 0 ? (
                    <>
                      {categoryBreakdown.map((item) => (
                        <tr key={item.label} className="border-bottom bg-white">
                          <td className="py-3 px-4 fw-bold text-dark text-14">{item.label}</td>
                          <td className="text-end text-14 text-dark">{this.formatCurrency(item.parts)}</td>
                          <td className="text-end text-14 text-dark">{this.formatCurrency(item.other)}</td>
                          <td className="fw-bold text-dark text-14 text-end pe-4">{this.formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                      <tr className="border-bottom bg-light">
                        <td className="py-3 px-4 fw-bold text-dark text-14">{t("total")}</td>
                        <td className="text-end text-14 fw-bold text-dark">{this.formatCurrency(totalParts)}</td>
                        <td className="text-end text-14 fw-bold text-dark">{this.formatCurrency(totalOther)}</td>
                        <td className="fw-bold text-dark text-14 text-end pe-4">{this.formatCurrency(totalCost)}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center text-secondary py-4">{t("no_data")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  }
}
